import {distinct, formatCents, selectMany, useAsyncAction} from "nate-react-api-helpers";
import {useTableData} from "../../../misc/scroller/Table";
import {purchaseOrderItemsTableName} from "./Items";
import {PoLinkedProject, PurchaseOrder, PurchaseOrderItem} from "../../../api/Logistics";
import {css} from "@emotion/css";
import {grey} from "@mui/material/colors";
import {
    Alert,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid
} from "@mui/material";
import {Dialog2} from "../../../misc/Dialog2";
import {useContext, useState} from "react";
import {PrintManagerContext} from "../../../misc/pdf/PrintManager";
import {MenuButton} from "./MenuButton";
import { downloadCSV } from "./csvutil";
import {api} from "../../../api/API";
import {DoorElevationDetail, purchaseOrderCSV, PurchaseOrderPDF, taxRate} from "./pdf/PurchaseOrderPDF";
import {useSnackbar} from "../../../misc/Snackbar";
import {blankPrep} from "../../project/shopdrawing/extras/PrepMenuOptions";
import {doorDetailsCSV, makeDoorDetails, useDoorDetails} from "./pdf/DoorDetails";
import {frameDetailsCSV, makeFrameDetails, useFrameDetails} from "./pdf/FrameDetails";
import {getHwItemDetail} from "./pdf/HardwarePrepTable";
import {makeHwPrepDiagrams} from "./pdf/MakeHwPrepDiagrams";
import {configToPerOpening} from "../../project/shopdrawing/keying/submittal/KeyingSubmittal";
import {Link} from "react-router-dom";
import {KeyDetails2} from "./pdf/KeyingDetails";
import {GlazingDetail} from "./pdf/GlazingDetails";
import {resolveGlazingSections} from "../../project/shopdrawing/glazingSubmittal/GlazingSections";
import {getFrameCutPlanForPurchaseOrder, NO_CUT_PLANS_ERROR} from "../../project/shopdrawing/frameCutPlan/FrameCutPlanPDF";
import {SubmittalRenderGate} from "../../project/shopdrawing/openings/SubmittalRenderGate";
import {RenderingStatus} from "../../../api/ElevationTool";
import {SnackCancel, useSnackAction} from "../../project/shopdrawing/hardware/SnackAction";

const blankArray = [] as any;

export function ItemsFooter(props: {
    value: PurchaseOrder;
}) {
    const data = useTableData<PurchaseOrderItem>(purchaseOrderItemsTableName)
    const subTotal = data.reduce((acc, value) => acc + value.unitPriceCents * value.qty, 0);

    const taxAmount = Math.round(subTotal * taxRate / 100);
    const total = subTotal + taxAmount;

    const ctx = useContext(PrintManagerContext);
    const snack = useSnackbar();

    const frameDetails = useFrameDetails(data, blankArray);
    const doorDetails = useDoorDetails(data, blankArray);

    const [doExport, setDoExport] = useState<{
        callback: (includePricing: boolean) => Promise<void>,
    }>();

    const [doPriceRequestPDFExport, setDoPriceRequestPDFExport] = useState<boolean>();

    const doExportAction = useAsyncAction(async (input: Promise<void>) => {
        await input;
        setDoExport(undefined)
        setDoPriceRequestPDFExport(undefined);
    }, []);

    const doPDFExport = async (includePricing: boolean, priceRequest: boolean) => {
        try {
            const cutTemplates = distinct(data
                .filter(item => item.kind !== "hardware")
                .filter(item => {
                    // ignore cut templates when the frame has a blank prep
                    if(item.kind !== "frame") return true;

                    if(item.framePreps[blankPrep]) return false;
                    return true;
                })
                .map(item => item.hwPrepCutFile)
                .filter(v => !!v)) as number[];

            const prepTemplates = distinct(selectMany(data
                .map(item => {
                    if(!item.openings) return [];

                    if(item.kind === "frame") {
                        return Object.values(item.framePreps).map(p => p.type === "custom" ? p.file : 0).filter(v => !!v) as number[];
                    } else if(item.kind === "door") {
                        return Object.values(item.doorPreps).map(p => p.type === "custom" ? p.file : 0).filter(v => !!v) as number[];
                    }

                    return []
                }), v => v))

            const screenElevations = distinct(data
                .map(item => item.kind === "frame" ? item.screenElevationFile || 0 : 0)
                .filter(v => !!v)) as number[];


            let distinctDoorElevNames: string[] = []
            const doorElevationsRaw = data
                .map(item => {
                    if(item.kind !== "door") return null;
                    if(!item.doorElevation) return null;

                    return {
                        elevation: item.doorElevation,
                        elevationFile: item.doorElevationFile,
                        elevationFileContent: null,
                        profile: item.doorLiteKitProfileFile,
                        profileContent: null
                    }
                }).filter(v => {
                    if(!v) return false;
                    if(distinctDoorElevNames.includes(v.elevation)) return false;
                    distinctDoorElevNames.push(v.elevation);
                    return true;
                }) as DoorElevationDetail[];

            const doorElevationsP = Promise.all(doorElevationsRaw.map(async r => {
                if(r.elevationFile) {
                    r.elevationFileContent = await api.print.fileAsImage({file: r.elevationFile});
                }

                if(r.profile) {
                    r.profileContent = await api.print.fileAsImage({file: r.profile});
                }

                return r;
            }));

            // the glazing attachment: the elevation each pane was measured on, with its glass table.
            let glazing: GlazingDetail | undefined;
            if(data.some(item => item.kind === "glazing")) {
                glazing = await resolveGlazingSections(await api.elevationTool.glazingForPurchaseOrder({id: props.value.id}));
            }

            const keyingInfo = await api.keying2.getDetailsForPO({id: props.value.id});

            let keying: KeyDetails2 = {
                rows: [],
                names: [],
            };

            if(keyingInfo) {
                keying.rows = configToPerOpening(keyingInfo);
                keying.names = keyingInfo.labelsAvailable || [];
            }

            const po = await api.logistics.getPO({id: props.value.id});

            if(!po.submittedAt && !priceRequest) {
                throw new Error("Missing 'Submitted At' date")
            }

            if(!po.targetPickupDate && !priceRequest) {
                throw new Error("Missing 'Target' date")
            }

            const frames = makeFrameDetails(data);
            const doors = makeDoorDetails(data)
            const hardwareDetail = getHwItemDetail(frames, doors);
            const prepDiagrams = await makeHwPrepDiagrams(hardwareDetail);
            const doorElevations = await doorElevationsP;

            const fname = priceRequest ? `price-request-${po.poNumber}.pdf` : `PO${po.poNumber}.pdf`;

            await ctx.print(fname,
                async onReady => <PurchaseOrderPDF value={po}
                                                   items={data}
                                                   hardwareDetail={hardwareDetail}
                                                   prepDiagrams={prepDiagrams}
                                                   onReady={onReady}
                                                   keyDetails={keying}
                                                   doorElevations={doorElevations}
                                                   glazing={glazing}
                                                   includePricing={includePricing}
                                                   priceRequest={priceRequest}
                />, {
                    appendFiles: [
                        ...cutTemplates,
                        ...screenElevations,
                        ...prepTemplates,
                    ]
                })
        } catch (e: any) {
            snack.error(e.toString())
        }
    }

    const hasFrames = data.some(item => item.kind === "frame");

    // the cut plan reads the elevation renderings, so bring the linked projects' renderings up to
    // date first and hold on the gate while any are still pending or failed. Proceeding past the
    // gate skips that check.
    const [cutPlanGate, setCutPlanGate] = useState<{projectId: number; status: RenderingStatus} | null>(null);

    const exportCutPlan = useSnackAction(async (skipGate: boolean) => {
        if(!skipGate) {
            for(const project of props.value.linkedProjects ?? []) {
                await api.elevationTool.refreshRenderings({project: project.id});
                const status = await api.elevationTool.renderingStatus({project: project.id});
                if(status.pending > 0 || status.failed > 0) {
                    setCutPlanGate({projectId: project.id, status});
                    return SnackCancel;
                }
            }
        }

        const byProject = await api.elevationTool.frameCutPlansForPurchaseOrder({id: props.value.id});
        if(!byProject.some(p => p.groups.length > 0)) throw new Error(NO_CUT_PLANS_ERROR);

        await ctx.print2(getFrameCutPlanForPurchaseOrder({purchaseOrder: props.value, byProject}));
    }, {success: "Frame cut plan ready"});

    const recalc = props.value.linkedProjects?.find(p => p.needsRecalculateProducts) as PoLinkedProject | undefined;

    return (
        <div>
            <table className={tableCls} style={{width: "100%", backgroundColor: "white"}}>
                <tbody>
                <tr>
                  <td></td>
                  <td style={{width: 100}}>Taxes</td>
                  <td style={{width: 50}}>{taxRate}%</td>
                  <td style={{width: 80}}>{formatCents(taxAmount)}</td>
                </tr>
                <tr>
                  <td></td>
                  <td>Total</td>
                  <td></td>
                  <td>{formatCents(total)}</td>
                </tr>
                </tbody>
            </table>
            {recalc && <div>
                <Alert severity="error" style={{
                    display: "flex",
                    alignItems: "center",
                }}>
                    Project has pending product edits which may affect this PO <Link to={`/project/${recalc.id}/shop-drawing`}><Button>View</Button></Link>
                </Alert>
            </div>}

            <div style={{padding: 8, backgroundColor: grey["200"]}}>
                <Grid container justifyContent="flex-end" spacing={1}>
                    <Grid item>
                        {doExport && <Dialog2 open onClose={() => setDoExport(undefined)} maxWidth="sm" fullWidth>
                            <DialogTitle>
                                Export PO
                            </DialogTitle>
                            <DialogContent>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <Button fullWidth variant="contained" onClick={() => {
                                            doExportAction.callback(doExport?.callback(true) || Promise.resolve());
                                        }}>With Pricing</Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button fullWidth variant="contained"
                                            onClick={() => {
                                                doExportAction.callback(doExport?.callback(false) || Promise.resolve());
                                            }}>Without Pricing</Button>
                                    </Grid>
                                </Grid>
                                {doExportAction.LoadingElement}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setDoExport(undefined)}>Cancel</Button>
                            </DialogActions>
                        </Dialog2>}
                        {doPriceRequestPDFExport && <Dialog2 open onClose={() => setDoPriceRequestPDFExport(undefined)} maxWidth="sm" fullWidth>
                            <DialogTitle>
                                Export Price Request
                            </DialogTitle>
                            <DialogContent>
                                {doExportAction.LoadingElement}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setDoPriceRequestPDFExport(undefined)}>Cancel</Button>
                            </DialogActions>
                        </Dialog2>}
                        <MenuButton
                                options={[{
                                    name: "Export PDF",
                                    onClick: async () => {
                                        setDoExport({
                                            callback: (includePricing) => {
                                                return doPDFExport(includePricing, false)
                                            }
                                        });
                                    },
                                }, {
                                    name: "Export Price Request PDF",
                                    onClick: async () => {
                                        setDoPriceRequestPDFExport(true);
                                        doExportAction.callback(doPDFExport(false, true))
                                    },
                                }, {
                                    name: "Export CSV",
                                    onClick: () => {
                                        setDoExport({
                                            callback: async (includePricing) => {
                                                downloadCSV(`PO${props.value.poNumber}.csv`, purchaseOrderCSV({
                                                    items: data,
                                                    value: props.value,
                                                    includePricing: includePricing,
                                                }))
                                            }
                                        });
                                    },
                                }, {
                                    name: "Frame Cut Plan PDF",
                                    disabled: !hasFrames,
                                    onClick: () => exportCutPlan.callback(false),
                                }, {
                                    name: "Door Detail CSV",
                                    disabled: doorDetails.length === 0,
                                    onClick: () => {
                                        downloadCSV(`PO${props.value.poNumber}-door-detail.csv`, doorDetailsCSV(doorDetails))
                                    },
                                }, {
                                    name: "Frame Detail CSV",
                                    disabled: frameDetails.length === 0,
                                    onClick: () => {
                                        downloadCSV(`PO${props.value.poNumber}-frame-detail.csv`, frameDetailsCSV(frameDetails))
                                    },
                                }]}
                        >
                            Export
                        </MenuButton>
                        {cutPlanGate && <SubmittalRenderGate
                            projectId={cutPlanGate.projectId}
                            status={cutPlanGate.status}
                            onProceed={() => {
                                setCutPlanGate(null);
                                exportCutPlan.callback(true);
                            }}
                            onCancel={() => setCutPlanGate(null)}
                        />}
                    </Grid>
                </Grid>
            </div>
        </div>
    )
}

const tableCls = css({
    fontSize: 13,
    borderCollapse: "collapse",

    "& td": {
        border: "1px solid " + grey["300"],
        paddingLeft: 8,
        paddingRight: 8,
    },

    "& td:not(:first-child)": {
        textAlign: "right",
    }
})